import React, { useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons'
import './SearchDocumentsPagination.css'
import { useSearchSalesDocumentsContext } from 'state/context/SearchSalesDocumentsContext'

const WINDOW_SIZE = 3

export default function SearchDocumentsNumberedPagination() {
  const {
    filters,
    loadingData,
    totalPages,
    totalRecords,
    searchDocuments,
    pageWindowFirst,
    pageWindowLast,
    setPageWindowFirst,
    setPageWindowLast,
  } = useSearchSalesDocumentsContext()

  const currentPage = filters.pageNumber
  const pageNums = Array.from({ length: totalPages }, (_, i) => i + 1)

  // Reset window when page resets to 1 (e.g. after Filtrar / Limpiar)
  useEffect(() => {
    if (currentPage === 1) {
      setPageWindowFirst(0)
      setPageWindowLast(WINDOW_SIZE)
    }
  }, [currentPage, setPageWindowFirst, setPageWindowLast])

  const fetchPage = async newPage => {
    if (loadingData || newPage < 1 || newPage > totalPages) return
    const nextFilters = { ...filters, pageNumber: newPage }
    await searchDocuments(nextFilters, { syncFilters: true })
  }

  const handlePageClick = e => {
    const page = parseInt(e.target.value)
    setPageWindowFirst(page - 1)
    setPageWindowLast(page - 1 + WINDOW_SIZE)
    fetchPage(page)
  }

  const handlePrevPage = () => {
    if (currentPage <= 1) return
    const newPage = currentPage - 1
    setPageWindowFirst(prev => Math.max(0, prev - 1))
    setPageWindowLast(prev => Math.max(WINDOW_SIZE, prev - 1))
    fetchPage(newPage)
  }

  const handleNextPage = () => {
    if (currentPage >= totalPages) return
    const newPage = currentPage + 1
    setPageWindowFirst(prev =>
      Math.min(pageNums.length - WINDOW_SIZE, prev + 1)
    )
    setPageWindowLast(prev => Math.min(pageNums.length, prev + 1))
    fetchPage(newPage)
  }

  const handleReturnFirst = () => {
    setPageWindowFirst(0)
    setPageWindowLast(WINDOW_SIZE)
    fetchPage(1)
  }

  const handleJumpForward = () => {
    const newFirst = pageWindowLast
    const newLast = Math.min(pageNums.length, pageWindowLast + WINDOW_SIZE)
    const newPage = pageWindowLast + 1
    setPageWindowFirst(newFirst)
    setPageWindowLast(newLast)
    fetchPage(newPage)
  }

  const handleChangePageSize = event => {
    const newPageSize = +event.target.value
    setPageWindowFirst(0)
    setPageWindowLast(WINDOW_SIZE)
    const nextFilters = { ...filters, pageSize: newPageSize, pageNumber: 1 }
    searchDocuments(nextFilters, { syncFilters: true })
  }

  const startEntry =
    totalRecords > 0 ? (currentPage - 1) * filters.pageSize + 1 : 0
  const endEntry = Math.min(currentPage * filters.pageSize, totalRecords)

  return (
    <div className="pagination-container">
      <div className="common-search-footer">
        <div className="cs-footer-content">
          <p>
            {totalRecords > 0
              ? `Mostrando ${startEntry} al ${endEntry} de ${totalRecords} entradas`
              : 'Sin resultados'}
          </p>

          <select
            data-testid="input-pagesize-quotes"
            name="changePageSize"
            id="pageSize_selector"
            onChange={handleChangePageSize}
            value={filters.pageSize}>
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
        </div>
      </div>

      <div className="sq-pagination">
        <FontAwesomeIcon
          data-testid="btn-prev-page-quotes"
          className={`sq-chevron${
            currentPage <= 1 || loadingData ? ' disabled' : ''
          }`}
          icon={faChevronLeft}
          onClick={handlePrevPage}
        />

        {pageWindowFirst > 0 && (
          <button className="sq-page-regular" onClick={handleReturnFirst}>
            ...
          </button>
        )}

        {pageNums.slice(pageWindowFirst, pageWindowLast).map(pageNum => (
          <button
            data-testid={`page-button-${pageNum}`}
            key={pageNum}
            className={
              currentPage === pageNum ? 'sq-page-active' : 'sq-page-regular'
            }
            value={pageNum}
            onClick={handlePageClick}
            disabled={loadingData}>
            {pageNum}
          </button>
        ))}

        {pageWindowLast < pageNums.length && (
          <button className="sq-page-regular" onClick={handleJumpForward}>
            ...
          </button>
        )}

        <FontAwesomeIcon
          data-testid="btn-next-page-quotes"
          className={`sq-chevron${
            currentPage >= totalPages || loadingData ? ' disabled' : ''
          }`}
          icon={faChevronRight}
          onClick={handleNextPage}
        />
      </div>
    </div>
  )
}
